/**
 * Is a KYC tile row ticked, given the flow's saved settings and the catalog row
 * that prices it?
 *
 * The tile has always read an UNSET key as ticked ("unset means on"), which is
 * right for the checks that ship `default: "true"` and were free or near-free.
 * It is not right for a $5 opt-in search: a flow saved before that row existed
 * has no key for it at all, and "unset means on" would tick it, quote it, and
 * bill it without anyone choosing it.
 *
 * So the rule is narrowed to exactly what it is for — never charge for a box
 * nobody ticked — rather than reading the catalog's `default` for every row.
 * Doing that would also flip `ssnVerification`, which ships `default: "false"`
 * in the catalog but has always drawn ticked and costs nothing; changing what
 * that row publishes is a product decision, not a side effect of adding a
 * different check.
 */
export const rowDefaultOn = (meta: {price?: unknown; default?: unknown} | undefined): boolean => {
	const priced = Number(meta?.price ?? 0) > 0;
	return !(priced && String(meta?.default) === 'false');
};

/** The tri-state read: explicit true/false wins, unset falls to the default. */
export const isRowOn = (
	saved: unknown,
	meta: {price?: unknown; default?: unknown} | undefined
): boolean => (typeof saved === 'boolean' ? saved : rowDefaultOn(meta));
